import React, { useState, useContext } from 'react';
import { FaTrash, FaUpload, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import styles from './ImageManager.module.css';
import { ToastContext } from '~/components/context/ToastContext';
import { ModalContext } from '~/components/context/ModalContext';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

const ImageManager = () => {
  const navigate = useNavigate();
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);
  const [mode, setMode] = useState('delete');
  const [endDate, setEndDate] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleFileChange = (e) => {
    setFiles(e.target.files);
    setErrors([]);
  };

  const handleDeleteImages = async () => {
    if (!endDate) {
      showToast('Vui lòng điền ngày kết thúc', 'error');
      setErrors(['Vui lòng điền ngày kết thúc']);
      return;
    }

    try {
      setLoading(true);
      setErrors([]);
      const response = await fetch('https://xanh19.com/delete_images.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          end_date: endDate,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorData;
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
          setErrors(errorData.details || [errorData.error || `Lỗi HTTP! Status: ${response.status}`]);
          throw new Error(errorData.error || `Lỗi HTTP! Status: ${response.status}`);
        } else {
          const text = await response.text();
          console.error('Non-JSON response:', { status: response.status, contentType, text });
          throw new Error('Máy chủ trả về định dạng không hợp lệ');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `đã_xóa_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast('Xóa ảnh và tải xuống ZIP thành công', 'success');
    } catch (err) {
      console.error('Delete error:', err);
      const errorMessage = err.message.includes('Failed to fetch')
        ? 'Lỗi kết nối máy chủ (có thể do HTTP/2 hoặc CORS). Vui lòng thử lại hoặc kiểm tra cấu hình máy chủ.'
        : err.message || 'Lỗi khi xóa ảnh';
      showToast(errorMessage, 'error');
      setErrors([errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadImages = async () => {
    if (files.length === 0) {
      showToast('Vui lòng chọn ít nhất một tệp', 'error');
      setErrors(['Vui lòng chọn ít nhất một tệp']);
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files[]', files[i]);
    }

    try {
      setLoading(true);
      setErrors([]);
      const response = await fetch('https://xanh19.com/upload_images.php', {
        method: 'POST',
        body: formData,
      });

      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', { status: response.status, contentType, text });
        throw new Error('Máy chủ trả về định dạng không hợp lệ');
      }

      if (response.ok) {
        showToast(data.message || 'Tải lên ảnh thành công', 'success');
        setErrors([]);
      } else {
        setErrors(data.details || [data.error || 'Lỗi khi tải lên ảnh']);
        showToast(`${data.error || 'Lỗi khi tải lên ảnh'}${data.details ? ': ' + data.details.join(', ') : ''}`, 'error');
      }
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err.message.includes('Failed to fetch')
        ? 'Lỗi kết nối máy chủ (có thể do HTTP/2 hoặc CORS). Vui lòng thử lại hoặc kiểm tra cấu hình máy chủ.'
        : err.message || 'Lỗi kết nối đến máy chủ';
      showToast(errorMessage, 'error');
      setErrors([errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const confirmAction = () => {
    if (mode === 'delete') {
      showModal({
        title: 'Xác Nhận Xóa Ảnh',
        message: `Bạn có chắc chắn muốn xóa tất cả ảnh trước ngày ${endDate}?`,
        onConfirm: handleDeleteImages,
        onCancel: null,
      });
    } else {
      showModal({
        title: 'Xác Nhận Tải Lên Ảnh',
        message: `Bạn có chắc chắn muốn tải lên ${files.length} tệp?`,
        onConfirm: handleUploadImages,
        onCancel: null,
      });
    }
  };

  return (
    <div className={cx('container')}>
      <div className={cx('content')}>
        <div className={cx('header')}>
          <h2 className={cx('title')}>{mode === 'delete' ? 'Xóa Ảnh Báo Cáo' : 'Tải Lên Ảnh Báo Cáo'}</h2>
        </div>

        <div className={cx('month-section')}>
          <div className={cx('month-content')}>
            <div className={cx('upload-container')}>
              <div className={cx('month-header')}>
                <h3 className={cx('subtitle')}>
                  {mode === 'delete' ? 'Chọn ngày kết thúc' : 'Chọn tệp để tải lên'}
                </h3>
                <div>
                  <label className={cx('upload-text')}>Chế độ:</label>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    className={cx('select-input')}
                    disabled={loading}
                  >
                    <option value="delete">Xóa Ảnh</option>
                    {/* <option value="upload">Tải Lên Ảnh</option> */}
                  </select>
                </div>
              </div>
              <div className={cx('month-content')}>
                <div className={cx('form-group')}>
                  {mode === 'delete' && (
                    <div>
                      <label className={cx('upload-text')}>Ngày kết thúc (YYYY-MM-DD):</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className={cx('file-input')}
                        disabled={loading}
                      />
                    </div>
                  )}
                  {mode === 'upload' && (
                    <div>
                      <label className={cx('upload-text')}>Chọn ảnh hoặc tệp ZIP:</label>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,application/zip"
                        multiple
                        onChange={handleFileChange}
                        className={cx('file-input')}
                        disabled={loading}
                      />
                      {errors.length > 0 && (
                        <ul className={cx('error-list')}>
                          {errors.map((error, index) => (
                            <li key={index} className={cx('error-item')}>{error}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <button
                    className={cx('action-button', mode === 'delete' ? 'delete' : 'upload')}
                    onClick={confirmAction}
                    disabled={loading}
                    aria-label={mode === 'delete' ? 'Xóa và tải ảnh' : 'Tải lên ảnh'}
                  >
                    {loading ? (
                      <>
                        <FaSpinner className={cx('spinner')} />
                        {mode === 'delete' ? 'Đang xử lý...' : 'Đang tải lên...'}
                      </>
                    ) : (
                      <>
                        {mode === 'delete' ? <FaTrash /> : <FaUpload />}
                        {mode === 'delete' ? 'Xóa và Tải ZIP' : 'Tải Lên'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageManager;